/**
 * PhotoHeroBatchResult - Full-screen photo-hero layout for batch (multi-dice)
 * capture results. The captured photo fills the container as background with
 * dice results shown as floating transparent overlays.
 * @module PhotoHeroBatchResult
 */

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { ValueEditSheet } from '@/components/ValueEditSheet';
import { cn } from '@/lib/utils';
import type { DetectedDie } from '@/services/recognition';

/** Props for the PhotoHeroBatchResult component. */
export interface PhotoHeroBatchResultProps {
  /** Captured batch image as data URL */
  imageData: string;
  /** Array of detected dice with their values */
  dice: (DetectedDie & { wasEdited?: boolean; originalValue?: number })[];
  /** Expected number of dice */
  expectedCount?: number;
  /** Detection warnings */
  warnings?: string[];
  /** Called when user edits a die value */
  onDieEdit?: (index: number, newValue: number) => void;
  /** Action buttons rendered in floating bottom bar */
  actions: React.ReactNode;
  className?: string;
}

/** Returns a CSS color class for a confidence value. */
function confidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'bg-green-400';
  if (confidence >= 0.5) return 'bg-amber-400';
  return 'bg-red-400';
}

/**
 * Full-screen photo-hero layout for displaying batch dice capture results.
 * Photo fills the background; dice values appear as tappable glass pills
 * with a total sum, warning banner, and action bar.
 */
export function PhotoHeroBatchResult({
  imageData,
  dice,
  expectedCount,
  warnings,
  onDieEdit,
  actions,
  className
}: PhotoHeroBatchResultProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [editingDieIndex, setEditingDieIndex] = useState<number | null>(null);

  const total = dice.reduce((sum, d) => sum + d.value, 0);
  const hasCountMismatch = expectedCount !== undefined && dice.length !== expectedCount;
  const hasWarnings = (warnings && warnings.length > 0) || hasCountMismatch;

  const warningText = hasCountMismatch
    ? `Expected ${expectedCount} dice, found ${dice.length}`
    : warnings?.[0];

  return (
    <div className={cn('relative h-full w-full overflow-hidden rounded-lg', className)}>
      {/* Photo background */}
      <button
        type="button"
        className="absolute inset-0 w-full h-full cursor-pointer bg-black"
        onClick={() => imageData && setLightboxOpen(true)}
        aria-label="View full image"
      >
        {imageData && (
          <img src={imageData} alt="Captured dice" className="h-full w-full object-cover" />
        )}
      </button>

      {/* Warning banner */}
      {hasWarnings && (
        <div className="absolute top-0 left-0 right-0 z-10 p-2">
          <div className="flex items-center gap-2 mx-2 mt-2 p-2 rounded-lg backdrop-blur-sm bg-red-900/60 border border-red-500/30 text-white text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{warningText}</span>
          </div>
        </div>
      )}

      {/* Dice count badge */}
      <Badge className="absolute top-2 right-2 z-10 backdrop-blur-sm bg-black/50 text-white text-xs rounded-full px-3 py-1.5 border-0">
        {dice.length} dice
      </Badge>

      {/* Dice summary overlay */}
      <div className="absolute left-1/2 -translate-x-1/2 z-10 bottom-24 w-[90%] max-w-sm">
        <div className="backdrop-blur-sm bg-black/60 rounded-2xl p-4 border border-white/20">
          {/* Dice pills */}
          <div className="flex flex-wrap justify-center gap-2">
            {dice.map((die, index) => {
              const label = `${(die.diceType ?? 'd?').toUpperCase()}: ${die.value}`;
              const pill = (
                <>
                  {label}
                  <span
                    className={cn(
                      'inline-block w-1.5 h-1.5 rounded-full ml-1',
                      confidenceColor(die.confidence)
                    )}
                  />
                  {die.wasEdited && (
                    <span className="ml-1 text-[10px] text-white/50 italic">Edited</span>
                  )}
                </>
              );
              const pillClasses = cn(
                'backdrop-blur-sm bg-white/10 rounded-full px-3 py-1.5 text-sm text-white border border-white/10 inline-flex items-center',
                onDieEdit && 'cursor-pointer hover:bg-white/20'
              );

              return onDieEdit ? (
                <button
                  key={index}
                  type="button"
                  className={pillClasses}
                  onClick={() => setEditingDieIndex(index)}
                >
                  {pill}
                </button>
              ) : (
                <span key={index} className={pillClasses}>
                  {pill}
                </span>
              );
            })}
          </div>

          {/* Total */}
          {dice.length > 1 && (
            <div className="text-center mt-3 pt-3 border-t border-white/20">
              <span className="text-xs text-white/60 uppercase tracking-wide">Total</span>
              <div className="text-4xl font-bold text-white text-glow">{total}</div>
            </div>
          )}
        </div>
      </div>

      {/* Action bar */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-4 backdrop-blur-md bg-black/50 border-t border-white/10">
        {actions}
      </div>

      {/* Image lightbox */}
      <ImageLightbox
        imageUrl={imageData}
        alt="Captured dice (full size)"
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {/* Value edit sheet */}
      {editingDieIndex !== null && (
        <ValueEditSheet
          open={editingDieIndex !== null}
          onOpenChange={(open) => {
            if (!open) setEditingDieIndex(null);
          }}
          value={dice[editingDieIndex].value}
          diceType={dice[editingDieIndex].diceType ?? 'd6'}
          onValueEdit={(newValue) => {
            onDieEdit?.(editingDieIndex, newValue);
            setEditingDieIndex(null);
          }}
        />
      )}
    </div>
  );
}

export default PhotoHeroBatchResult;
